import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating (reused)
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge (reused)
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

const AngularJsCourse = () => {
  const testimonials = [
    {
      id: 1,
      name: "Rahul Saxena",
      review:
        "The AngularJS training at Nadezhda Venture was outstanding! The deep dive into directives and services helped me immensely in my front-end development role. Highly recommended for anyone looking to master AngularJS.",
      rating: 5,
      date: "2024-06-01",
      profileImg: "https://randomuser.me/api/portraits/men/33.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 2,
      name: "Preeti Agarwal",
      review:
        "I was struggling with client-side MVC, but this course clarified everything. The practical examples and project work were super helpful. Best AngularJS course in Noida, hands down!",
      rating: 4,
      date: "2024-05-28",
      profileImg: "https://randomuser.me/api/portraits/women/46.jpg", // Placeholder image
      source: "LinkedIn",
    },
    {
      id: 3,
      name: "Deepak Singh",
      review:
        "The instructors are experts and explained complex concepts like two-way data binding perfectly. I'm now confidently building single-page applications. Great value for money!",
      rating: 5,
      date: "2024-05-25",
      profileImg: "https://randomuser.me/api/portraits/men/51.jpg", // Placeholder image
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Anjali Verma",
      review:
        "Coming from a design background, learning a framework like AngularJS seemed daunting. But the structured modules and hands-on approach made it easy. Got a front-end developer job after this course!",
      rating: 5,
      date: "2024-05-20",
      profileImg: "https://randomuser.me/api/portraits/women/61.jpg", // Placeholder image
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Vikas Sharma",
      review:
        "Comprehensive coverage of AngularJS from basics to advanced topics. The support for project implementation and debugging was exceptional. Truly prepares you for real-world development.",
      rating: 4,
      date: "2024-05-18",
      profileImg: "https://randomuser.me/api/portraits/men/76.jpg", // Placeholder image
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "What is AngularJS?",
      answer:
        "AngularJS is an open-source JavaScript framework maintained by Google. It's used for building dynamic, single-page web applications (SPAs). It extends HTML's syntax to express your application's components clearly and concisely, making front-end development more structured and efficient.",
    },
    {
      title: "Who is this AngularJS course suitable for?",
      answer:
        "This course is ideal for JavaScript developers, front-end developers, web designers, and anyone looking to learn a powerful framework for building dynamic web applications. A basic understanding of HTML, CSS, and JavaScript is recommended.",
    },
    {
      title: "What key concepts will I learn in this course?",
      answer:
        "You will learn AngularJS core concepts like MVC architecture, data binding (two-way), directives, controllers, services, filters, modules, routing, dependency injection, forms, and unit testing with AngularJS. We also cover integrating with RESTful APIs.",
    },
    {
      title: "Is AngularJS still relevant compared to Angular (2+)?",
      answer:
        "While Angular (2+) is the successor, many large enterprise applications still use and maintain AngularJS. Knowing AngularJS is valuable for working on existing projects, maintenance, and migration. It also provides a strong foundation for understanding modern Angular frameworks.",
    },
    {
      title: "Will I get placement assistance after completing the course?",
      answer:
        "Yes, we provide 100% placement assistance for our AngularJS graduates. Our support includes resume building, mock interviews, soft skills training, and connecting you with companies actively hiring for AngularJS/Front-End Developer roles in Noida and across India.",
    },
    {
      title: "What kind of projects will I work on?",
      answer:
        "You will build several practical applications during the course, culminating in a major project. Examples include a task management application, an e-commerce dashboard, or a blog platform, allowing you to apply all learned concepts in a real-world scenario.",
    },
    {
      title: "What are the career opportunities after this training?",
      answer:
        "Graduates can pursue roles such as AngularJS Developer, Front-End Developer, Web Application Developer, UI Developer, and Software Engineer focusing on client-side frameworks.",
    },
    {
      title: "What is the duration and mode of training?",
      answer:
        "The course duration varies based on the pace (intensive fast-track vs. regular). We offer both intensive offline classroom training at our Noida center and interactive live online classes, providing flexibility to suit your schedule.",
    },
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const curriculumContent = [
    {
      title: "Module 1: Introduction to Web Applications & JavaScript Refresher",
      description: "Overview of client-side web development, single-page applications (SPAs), basics of HTML, CSS, and a comprehensive JavaScript (ES5/ES6) refresher for AngularJs.",
    },
    {
      title: "Module 2: AngularJS Fundamentals",
      description: "Introduction to AngularJS, why use it, MVC architecture, setting up the development environment, and your first AngularJS application.",
    },
    {
      title: "Module 3: Data Binding & Directives",
      description: "Understanding two-way data binding, built-in directives (ng-app, ng-model, ng-init, ng-repeat, ng-if, ng-show/hide, ng-class, ng-style) and creating custom directives.",
    },
    {
      title: "Module 4: Controllers & Scope",
      description: "Role of Controllers in AngularJS, understanding $scope, $rootScope, and managing data and logic within views.",
    },
    {
      title: "Module 5: Filters & Forms",
      description: "Using built-in filters (currency, date, orderBy), creating custom filters, handling forms, form validation, and `$http` for backend communication.",
    },
    {
      title: "Module 6: Services & Dependency Injection",
      description: "Concepts of Services, Factories, Providers, and Constants. In-depth understanding of Dependency Injection (DI) and its benefits in AngularJS.",
    },
    {
      title: "Module 7: Routing & Navigation",
      description: "Implementing client-side routing with `ngRoute` (or `ui-router`), managing application states, and deep linking for single-page applications.",
    },
    {
      title: "Module 8: Communicating with Backend (RESTful Services)",
      description: "Using `$http` service for AJAX calls, interacting with RESTful APIs, handling JSON data, and error management.",
    },
    {
      title: "Module 9: Advanced Concepts & Best Practices",
      description: "Modularizing applications, inter-component communication, understanding digest cycle, performance optimization, and AngularJS best practices.",
    },
    {
      title: "Module 10: Testing AngularJS Applications",
      description: "Introduction to unit testing with Karma and Jasmine, writing tests for controllers, services, and directives.",
    },
    {
      title: "Module 11: Real-World Project",
      description: "Develop a complete, interactive single-page application using all learned AngularJS concepts, integrating with a mock or real API. This project will be a significant addition to your portfolio.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2; // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(
    getVisibleTestimonialsCount()
  );

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(startIndex + i) % testimonials.length]
    );
  }

  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>AngularJS Course in Noida | Master Front-End Web Development</title>
        <meta
          name="description"
          content="Enroll in the best AngularJS course in Noida. Learn MVC, data binding, directives, services, routing, and build dynamic single-page applications with placement support."
        />
        <meta
          name="keywords"
          content="AngularJS training Noida, AngularJS course, learn AngularJS, front-end development, single-page application, SPA, JavaScript framework, AngularJS certification, UI developer course"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://samyak.com/wp-content/uploads/2024/07/Hire-the-Best-AngularJS-Development-Company-Samyak-Infotech-Pvt-Ltd.webp"
          alt="AngularJS Course Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
          <div className="md:w-1/2">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
            >
              Master <span className="text-red-400">AngularJS</span> in Noida
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-lg md:text-xl text-gray-200 text-left"
            >
              Build powerful and dynamic single-page applications (SPAs) with our hands-on AngularJS training program.
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-8 bg-orange-600 hover:bg-orange-400 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
            >
              <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("AngularJS Course Training")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Why Learn AngularJS Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn AngularJS?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            AngularJS revolutionized front-end development by introducing concepts like two-way data binding and dependency injection, making it easier to build complex web applications. It's still widely used in many enterprise-level projects.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Enterprise Adoption</h3>
              <p className="text-gray-600">Still powers numerous large-scale, critical applications.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Strong Foundation</h3>
              <p className="text-gray-600">Builds a solid understanding of modern front-end frameworks.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-red-700 mb-3">Modular & Testable</h3>
              <p className="text-gray-600">Designed for highly maintainable and testable codebases.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Hands-on SPA Development', desc: 'Build dynamic single-page applications from scratch with AngularJS.' },
              { icon: '💡', title: 'Master Core Concepts', desc: 'Deep dive into MVC, data binding, directives, and dependency injection.' },
              { icon: '👨‍🏫', title: 'Expert-Led Training', desc: 'Learn from experienced front-end developers and AngularJS specialists.' },
              { icon: '🎓', title: 'Industry-Recognized Certification', desc: 'Receive a professional certificate to enhance your resume.' },
              { icon: '🤝', title: 'Dedicated Placement Support', desc: 'Comprehensive assistance for resume building, mock interviews, and job placements.' },
              { icon: '🔄', title: 'Flexible Learning Modes', desc: 'Choose between dynamic offline classroom sessions or interactive online live classes.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-blue-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our AngularJS training ensures you gain proficiency in all essential tools and concepts for front-end development.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'AngularJS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg' },
              { name: 'JavaScript (ES5/ES6)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
              { name: 'HTML5', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
              { name: 'CSS3', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
              { name: 'Bootstrap', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg' },
              { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
              { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
              { name: 'Karma', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/karma/karma-plain.svg' },
              { name: 'NPM', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/npm/npm-original-wordmark.svg' },
              { name: 'Node.js (Basics)', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            An AngularJS certification from us will prepare you for various in-demand front-end development roles.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '💻', title: 'AngularJS Developer', desc: 'Specialize in building and maintaining applications using the AngularJS framework.' },
              { icon: '🌐', title: 'Front-End Developer', desc: 'Focus on implementing user interfaces and client-side logic for web applications.' },
              { icon: '✨', title: 'UI Developer', desc: 'Translate UI/UX designs into interactive and responsive web components.' },
              { icon: '🛠️', title: 'Web Application Developer', desc: 'Develop, test, and deploy robust web applications using various front-end technologies.' },
              { icon: '📈', title: 'Software Engineer (Front-End)', desc: 'Contribute to the design and implementation of large-scale front-end systems.' },
              { icon: '💡', title: 'Freelance Front-End Developer', desc: 'Offer your expertise to diverse clients as an independent contractor.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-blue-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-gray-100 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">
              Enquire Now
            </h3>
            <p className="text-center text-gray-600 mb-6">
              Fill out the form below to get more details about our AngularJS training program.
            </p>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="Course of Interest (e.g., AngularJS Course)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="text"
                placeholder="College/University Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
              >
                JOIN THE PROGRAM
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-red-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-red-900">
            What Our AngularJS Students Say
          </h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-red-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {testimonial.date} • {testimonial.source}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial && <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a
                        href="#"
                        className="text-red-600 hover:underline mt-4 text-sm text-right"
                      >
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  idx >= currentTestimonialIndex &&
                  idx < currentTestimonialIndex + visibleCount
                    ? "bg-red-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>

       {/* Conditionally render the popup */}
      {isPopupOpen && (
        <JoinNowFormPopup
          onClose={handleClosePopup}
          courseTitle={selectedCourseTitle}
        />
      )}
    </div>
  );
};

export default AngularJsCourse;